import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { personalInfoSchema } from "../schemas";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import AsyncSelect from "react-select/async";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { StylesConfig, CSSObjectWithLabel, GroupBase } from "react-select";
import {
  CityOption,
  loadCityOptions,
} from "../../../utils/cityAsyncSelectOptions";
export interface PersonalInfoData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  city: string;
  country?: string;
  personalPositionCompany: string;
  businessName: string;
  referralCodeUsed?: string;
}

interface PersonalInfoStepProps {
  defaultValues: PersonalInfoData;
  onNext: (data: PersonalInfoData) => void;
  /** When set, saves draft to DB while the user fills the form. */
  onAutoSave?: (data: PersonalInfoData) => Promise<void>;
}

type SelectStyles = StylesConfig<CityOption, false, GroupBase<CityOption>>;

const PersonalInfoStep: React.FC<PersonalInfoStepProps> = ({
  defaultValues,
  onNext,
  onAutoSave,
}) => {
  const { t } = useTranslation();
  const [showDiscountInput, setShowDiscountInput] = useState(false);
  const skipAutoSaveRef = useRef(true);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PersonalInfoData>({
    resolver: yupResolver(personalInfoSchema),
    defaultValues,
  });

  const watched = useWatch({ control });
  const cityValue = watch("city");
  const countryValue = watch("country");

  const citySelectValue = useMemo((): CityOption | null => {
    if (!cityValue?.trim()) return null;
    const city = cityValue.trim();
    const country = countryValue?.trim() ?? "";
    return {
      value: city,
      label: country ? `${city}, ${country}` : city,
      city,
      country,
    };
  }, [cityValue, countryValue]);

  const serializedDefaults = useMemo(
    () => JSON.stringify(defaultValues),
    [
      defaultValues.firstName,
      defaultValues.lastName,
      defaultValues.email,
      defaultValues.phoneNumber,
      defaultValues.city,
      defaultValues.country,
      defaultValues.personalPositionCompany,
      defaultValues.businessName,
      defaultValues.referralCodeUsed,
    ],
  );
  const lastHydratedDefaultsRef = useRef(serializedDefaults);

  useEffect(() => {
    if (lastHydratedDefaultsRef.current === serializedDefaults) {
      return;
    }
    lastHydratedDefaultsRef.current = serializedDefaults;
    reset(defaultValues);
    skipAutoSaveRef.current = true;
  }, [serializedDefaults, defaultValues, reset]);

  useEffect(() => {
    if (!onAutoSave) return;
    if (skipAutoSaveRef.current) {
      skipAutoSaveRef.current = false;
      return;
    }
    const t = window.setTimeout(() => {
      void onAutoSave(watched as PersonalInfoData);
    }, 1200);
    return () => window.clearTimeout(t);
  }, [watched, onAutoSave]);

  const onSubmit = (data: PersonalInfoData) => {
    const formattedData = {
      ...data,
    };
    onNext(formattedData);
  };

  const customSelectStyles: SelectStyles = {
    control: (
      base: CSSObjectWithLabel,
      state: { isFocused: boolean }
    ): CSSObjectWithLabel => ({
      ...base,
      borderColor: state.isFocused
        ? "#86b7fe"
        : errors.city
        ? "#dc3545"
        : base.borderColor || "#ced4da",
      boxShadow: state.isFocused
        ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
        : errors.city
        ? "0 0 0 0.2rem rgba(220,53,69,.25)"
        : base.boxShadow || "none",
      "&:hover": {
        borderColor: state.isFocused
          ? "#86b7fe"
          : errors.city
          ? "#dc3545"
          : base.borderColor || "#ced4da",
      },
      borderWidth: "1px",
      borderRadius: "0.375rem",
      minHeight: "38px",
      fontSize: "0.875rem",
    }),
    menu: (base: CSSObjectWithLabel): CSSObjectWithLabel => ({
      ...base,
      zIndex: 9999,
    }),
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-100">
      <h5 className="pb-2 d-block d-md-none">{t("account.section.personalDetails")}</h5>
      <div className="row g-2">
        <div className="col-md-6 mb-3">
          <div className="mb-2">
            <label className="form-label mb-0">
              {t("auth.firstName")} <span>*</span>
            </label>
          </div>
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                className="form-control"
                placeholder={t("registration.personalInfo.placeholder.firstName")}
              />
            )}
          />
          {errors.firstName && (
            <p className="custom-error-text mb-0 px-1 mt-1">
              {errors.firstName.message}
            </p>
          )}
        </div>
        <div className="col-md-6 mb-3">
          <div className="mb-2">
            <label className="form-label mb-0">
              {t("auth.lastName")} <span>*</span>
            </label>
          </div>
          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                className="form-control"
                placeholder={t("registration.personalInfo.placeholder.lastName")}
              />
            )}
          />
          {errors.lastName && (
            <p className="custom-error-text mb-0 px-1 mt-1">
              {errors.lastName.message}
            </p>
          )}
        </div>
        <div className="col-12 mb-3">
          <div className="mb-2">
            <label className="form-label mb-0">
              {t("common.emailAddress")} <span>*</span>
            </label>
          </div>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="email"
                className="form-control"
                placeholder={t("registration.personalInfo.placeholder.email")}
                readOnly
              />
            )}
          />
          {errors.email && (
            <p className="custom-error-text mb-0 px-1 mt-1">
              {errors.email.message}
            </p>
          )}
        </div>
        <div className="col-md-6 mb-3">
          <div className="mb-2">
            <label className="form-label mb-0">
              {t("registration.personalInfo.contactNo")}
            </label>
          </div>
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <div className="form-control p-0">
                <PhoneInput
                  {...field}
                  placeholder={t("registration.personalInfo.placeholder.phone")}
                  defaultCountry="GB"
                  value={field.value || ""}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                  className="phone-input w-100"
                />
              </div>
            )}
          />
          {errors.phoneNumber && (
            <p className="custom-error-text mb-0 px-1 mt-1">
              {errors.phoneNumber.message}
            </p>
          )}
        </div>
        <div className="col-md-6 mb-3">
          <div className="mb-2">
            <label className="form-label mb-0">
              {t("connections.city")} <span>*</span>
            </label>
          </div>
          <Controller
            name="city"
            control={control}
            render={({ field }) => (
              <AsyncSelect
                {...field}
                loadOptions={(inputValue, callback) => {
                  loadCityOptions(inputValue, callback);
                }}
                placeholder={t("registration.personalInfo.placeholder.city")}
                isClearable
                styles={customSelectStyles}
                classNamePrefix="react-select"
                value={citySelectValue}
                onChange={(selected: CityOption | null) => {
                  field.onChange(selected?.city ?? "");
                  setValue("country", selected?.country ?? "");
                }}
                defaultOptions={false}
                noOptionsMessage={({ inputValue }) => {
                  if (!inputValue || inputValue.trim().length === 0) {
                    return t("registration.personalInfo.placeholder.city");
                  }
                  return t("connections.noCitiesAvailable");
                }}
                loadingMessage={() => t("connections.loading")}
                cacheOptions
                isSearchable
                filterOption={null}
              />
            )}
          />
          {errors.city && (
            <p className="custom-error-text mb-0 px-1 mt-1">
              {errors.city.message}
            </p>
          )}
        </div>
        <div className="col-md-6 mb-3">
          <div className="mb-2">
            <label className="form-label mb-0">
              {t("explorer.signup.jobTitle")} <span>*</span>
            </label>
          </div>
          <Controller
            name="personalPositionCompany"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                className="form-control"
                placeholder={t("registration.personalInfo.placeholder.jobTitle")}
              />
            )}
          />
          {errors.personalPositionCompany && (
            <p className="custom-error-text mb-0 px-1 mt-1">
              {errors.personalPositionCompany.message}
            </p>
          )}
        </div>
        <div className="col-md-6 mb-3">
          <div className="mb-2">
            <label className="form-label mb-0">
              {t("explorer.signup.companyName")} <span>*</span>
            </label>
          </div>
          <Controller
            name="businessName"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                type="text"
                className="form-control"
                placeholder={t("registration.personalInfo.placeholder.companyName")}
              />
            )}
          />
          {errors.businessName && (
            <p className="custom-error-text mb-0 px-1 mt-1">
              {errors.businessName.message}
            </p>
          )}
        </div>
      </div>

      {/* Discount code section */}
      <div className="mt-3">
        {!showDiscountInput ? (
          <button
            type="button"
            className="btn btn-link p-0 text-start border-0 bg-transparent text-primary text-decoration-underline"
            onClick={() => setShowDiscountInput(true)}
          >
            {t("account.field.referralCode")}
          </button>
        ) : (
          <Form.Group>
            <Form.Label className="text-primary">
              {t("account.field.referralCode")}
            </Form.Label>
            <Controller
              name="referralCodeUsed"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  placeholder={t("registration.personalInfo.placeholder.referralCode")}
                  onChange={(e) => {
                    field.onChange(e);
                  }}
                />
              )}
            />
          </Form.Group>
        )}
      </div>

      <div className="d-flex justify-content-end mt-4">
        <button type="submit" className="btn btn-primary">
          {t("membership.next")}
        </button>
      </div>
    </form>
  );
};

export default PersonalInfoStep;